/**
 * src/components/HtmlPreview.tsx — the right-hand preview panel: a chrome bar and one iframe.
 *
 * Source: `D:/WCS/latex2html-french-trad/src/components/HtmlPreview.tsx` (whole file, 58 lines).
 * Spec:   `docs/spec/06-frontend-components.md` §5.2; §15 D-1, D-13, D-26; §16 ranks 4, 17, 18;
 *         and the domain note at the head of spec 06 (verse numbers live in the LEFT margin).
 *
 * ═══ DF-22 RANK 4 (spec D-1) — THE IFRAME IS REWRITTEN ON EVERY RENDER. REPRODUCED ═══════════
 * `HtmlPreview.tsx:37-39` runs `doc.open(); doc.write(fullHtml); doc.close();` inside an effect
 * keyed on `[html, css]` (`:12`, `:40`). `ConverterApp` recomputes `html`/`css` synchronously on
 * every keystroke with no debounce (`ConverterApp.tsx:112-113`), so each character:
 *   • destroys and recreates the whole iframe document ⇒ **scroll position jumps to the top**;
 *   • re-requests the KaTeX stylesheet from the CDN (`:25`).
 * On a scripture-length document this makes the preview unusable while typing. That is the point —
 * the owner is validating it visually. Do NOT debounce, do NOT diff/patch
 * `article.latex-document`, do NOT save and restore `scrollTop`, do NOT move conversion to a
 * Worker. Every one of those is the proposed fix, not the port.
 *
 * ═══ DF-22 RANK 17 (spec D-13) — THE "Live" BADGE IS FAKE. REPRODUCED ════════════════════════
 * The pulsing green dot and the word "Live" (`:46-49`) are static markup bound to no state. They
 * never indicate staleness, conversion-in-flight, or error. Do not wire them up.
 *
 * ═══ DF-22 RANK 18 (spec D-26) — THE IFRAME IS NOT SANDBOXED. REPRODUCED ═════════════════════
 * `:51-55` has **no `sandbox` attribute**: converted content is written same-origin with scripts
 * enabled. Combined with DF-9 (the converter performs no HTML escaping anywhere on the live path)
 * and CLAUDE.md §5 (a shared workspace where any authenticated user can edit any document), this
 * is a stored-XSS path between users. It is a real vulnerability and it is reproduced knowingly,
 * because the owner is validating defects before fixing them. `sandbox="allow-same-origin"` is the
 * one-attribute fix when it is approved — DF-22 rank 18, its own commit.
 *
 * ═══ ALSO REPRODUCED, LOWER SEVERITY ════════════════════════════════════════════════════════
 * • DF-19 — `lang="en"` (`:21`) on documents that are always French, and no `<title>`.
 * • DF-19 / spec §5.3 — the iframe pins KaTeX **0.16.9** from jsDelivr while `package.json` pins
 *   `katex@^0.16.25`; renderer and stylesheet can disagree.
 * • Spec §5.2 — `import 'katex/dist/katex.min.css'` (`:2`) pulls KaTeX CSS into the **parent app
 *   bundle**, where nothing renders KaTeX. Only the iframe does, and the iframe uses the CDN copy.
 *   The import is dead weight and is kept exactly because removing it is a fix.
 *
 * ═══ VERSE NUMBERS — THE HARD EDITORIAL REQUIREMENT (NOT A DEFECT) ══════════════════════════
 * `\Nverset` / `\Npar` / `\Nhidden` (`docs/reference/preambuleComTrad-20032026.tex:123`, `:127`,
 * `:131`) expand through `\marginpar` under `\reversemarginpar`, and the converter emits
 * `<span class="marginpar">` with `float: left` and
 * `margin-left: calc(-1 * var(--latex-marginpar-width) - 5mm)` (the `.marginpar` rule in
 * `src/lib/converter/css.ts`). The room that number pulls into comes from
 * `body { padding: 0 var(--latex-margin-right) }` in the SAME generated stylesheet
 * — i.e. it is provided **inside** this iframe, by `css`.
 *
 * Therefore: `{html}` must be written into `<body>` wrapped in nothing but
 * `<article class="latex-document">`, exactly as below. Adding a padding-less wrapper, a container
 * with `overflow-x: hidden`, or any left inset would clip verse numbers out of the page. The
 * `<iframe>` element itself carries no padding and no overflow rule for the same reason — the
 * iframe scrolls internally and never clips its own document.
 */
import { useEffect, useRef } from 'react'
import 'katex/dist/katex.min.css'

/** `HtmlPreview.tsx:4-7`. */
interface HtmlPreviewProps {
  html: string
  css: string
}

export function HtmlPreview({ html, css }: HtmlPreviewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe) return

    const doc = iframe.contentDocument || iframe.contentWindow?.document
    if (!doc) return

    // Build full HTML document with styles
    const fullHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.css">
  <style>
${css}
  </style>
</head>
<body>
  <article class="latex-document">
${html}
  </article>
</body>
</html>`

    // DF-22 rank 4 — full teardown/rebuild per render; scroll resets to top every keystroke.
    doc.open()
    doc.write(fullHtml)
    doc.close()
  }, [html, css])

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-secondary">
        <span className="text-xs text-muted-foreground font-mono">HTML Preview</span>
        <div className="flex items-center gap-1">
          {/* DF-22 rank 17 — hard-coded status indicator, bound to nothing. */}
          <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
          <span className="text-xs text-success">Live</span>
        </div>
      </div>
      {/* DF-22 rank 18 — no `sandbox` attribute, deliberately. */}
      <iframe ref={iframeRef} className="flex-1 w-full bg-white" title="LaTeX Preview" />
    </div>
  )
}

/*
 * ── DEPENDENCY OWNED BY ANOTHER FILE ────────────────────────────────────────────────────────
 * `bg-success` / `text-success` resolve through the source's Tailwind theme extension
 * `success: "hsl(var(--success))"` (`latex2html-french-trad/tailwind.config.ts:61`) backed by
 * `--success: 145 65% 42%` (`src/index.css:44`). Neither exists yet in
 * `D:/WCS/L2HC/tailwind.config.js` or `D:/WCS/L2HC/src/index.css`, so Tailwind emits no rule and
 * the "Live" dot renders invisible/unstyled. That is a porting gap in those two files, not the
 * reproduced DF-22 rank 17 defect (rank 17 is that the badge lies, not that it is missing).
 * Reported to the coordinator; not fixed here — neither file is assigned to this agent.
 */
